import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { FIVE_SECONDS } from '@/constants/time';
import { uploadSupportFile } from '@/services/conversations/attachments';
import { invalidateInboxDebounced } from '@/services/conversations/cache';
import { toConversationError, type ConversationError } from '@/services/conversations/errors';
import { conversationKeys } from '@/services/conversations/keys';
import type { SharedSurvey } from '@/services/conversations/sharedSurvey';
import {
  getSupportThreadQueryOptions,
  mergeThread,
  putSupportMessage,
  type LocalMessage,
  type MessageReference,
  type OutgoingFile,
  type SentMessageFile,
  type SupportThread,
} from '@/services/conversations/thread';
import { selectIsInboxLive, useInboxRealtimeStore } from '@/services/stores/inboxRealtimeStore';
import {
  trackSupportFileUploadCompleted,
  trackSupportFileUploadFailed,
} from '@/util/analyticsEvents';

/**
 * A support thread, kept current by the inbox socket's hints. While the view
 * is focused and the socket is not live, it polls instead, since hints may be
 * missed; it stops as soon as the socket is live again.
 */
export function useSupportThread(conversationId: string, { isFocused }: { isFocused: boolean }) {
  const queryClient = useQueryClient();

  const isLive = useInboxRealtimeStore(selectIsInboxLive);

  return useQuery({
    ...getSupportThreadQueryOptions(queryClient, conversationId),
    refetchInterval: isFocused && !isLive ? FIVE_SECONDS : false,
  });
}

export type SendSupportMessageVariables = {
  messageId: string;
  body: string;
  reference?: MessageReference;
  /** A survey to share; only Primum Support may. */
  survey?: SharedSurvey;
  files?: OutgoingFile[];
};

/**
 * Refusals of what a message carries, an inquiry or a survey, which a failed
 * bubble names so staff know to drop it.
 */
export const REFERENCE_REFUSAL_CODES: readonly string[] = [
  'REFERENCE_NOT_ALLOWED',
  'INVALID_REFERENCE',
  'INVALID_SURVEY',
  'SURVEY_SHARE_NOT_ALLOWED',
];

function withLocal(thread: SupportThread, message: LocalMessage): SupportThread {
  const others = thread.local.filter((candidate) => candidate.id !== message.id);

  return { ...thread, local: [...others, message] };
}

/**
 * Sends into a support thread under an id the caller generates when Send is
 * pressed, and reuses for a retry.
 *
 * The message shows at once as pending. A dropped connection is retried
 * automatically with the same id, which the server replays rather than posting
 * twice. A send that still fails stays in the thread as failed, for the reader
 * to retry or discard: unlike Ask, the text is not handed back to the
 * composer, because a conversation keeps its failed messages in place.
 *
 * `networkMode: 'always'` makes an offline send fail visibly instead of
 * waiting silently for the network.
 *
 * Files upload first, one at a time, and the message is sent once all have.
 * Each upload's key is kept on the pending message, so neither the automatic
 * retry nor the reader's Retry uploads the same file twice.
 */
export function useSendSupportMessage(conversationId: string) {
  const queryClient = useQueryClient();

  const key = conversationKeys.thread(conversationId);

  const update = useCallback(
    (updater: (thread: SupportThread) => SupportThread) =>
      queryClient.setQueryData<SupportThread>(conversationKeys.thread(conversationId), (current) =>
        current ? updater(current) : current
      ),
    [queryClient, conversationId]
  );

  /** Keys of files already uploaded, by file id, for the automatic retry's unchanged variables. */
  const uploadedKeys = useRef(new Map<string, string>());

  const uploadFiles = useCallback(
    async (messageId: string, files: readonly OutgoingFile[]): Promise<SentMessageFile[]> => {
      const sent: SentMessageFile[] = [];

      for (const file of files) {
        let s3Key = file.s3Key ?? uploadedKeys.current.get(file.id);

        if (!s3Key) {
          const uploadProperties = {
            conversation_id: conversationId,
            message_id: messageId,
            file_size: file.size,
            mime_type: file.mimeType,
          };

          let uploadedKey: string;

          try {
            uploadedKey = await uploadSupportFile(file);
          } catch (error) {
            trackSupportFileUploadFailed({
              ...uploadProperties,
              error_code: toConversationError(error).kind,
            });

            throw error;
          }

          trackSupportFileUploadCompleted(uploadProperties);

          uploadedKeys.current.set(file.id, uploadedKey);

          update((thread) => ({
            ...thread,
            local: thread.local.map((message) =>
              message.id === messageId
                ? {
                    ...message,
                    files: message.files?.map((candidate) =>
                      candidate.id === file.id ? { ...candidate, s3Key: uploadedKey } : candidate
                    ),
                  }
                : message
            ),
          }));

          s3Key = uploadedKey;
        }

        sent.push({ s3Key, fileName: file.name, mimeType: file.mimeType, source: file.source });
      }

      return sent;
    },
    [update, conversationId]
  );

  const mutation = useMutation<
    Awaited<ReturnType<typeof putSupportMessage>>,
    ConversationError,
    SendSupportMessageVariables
  >({
    mutationFn: async ({ messageId, body, reference, survey, files = [] }) =>
      putSupportMessage(
        conversationId,
        messageId,
        body,
        reference?.inquiryId,
        await uploadFiles(messageId, files),
        survey?.surveyGroupId
      ),
    networkMode: 'always',
    retry: (failureCount, error) => error.kind === 'network' && failureCount < 2,
    onMutate: async ({ messageId, body, reference, survey, files }) => {
      await queryClient.cancelQueries({ queryKey: key, exact: true });

      update((thread) =>
        withLocal(thread, {
          id: messageId,
          body,
          createdAt: new Date().toISOString(),
          status: 'pending',
          reference,
          survey,
          files,
        })
      );
    },
    onSuccess: ({ message }) => update((thread) => mergeThread(thread, thread.meta, [message])),
    onError: (error, { messageId }) =>
      update((thread) => ({
        ...thread,
        local: thread.local.map((message) =>
          message.id === messageId
            ? {
                ...message,
                status: 'failed',
                failureCode:
                  error.code && REFERENCE_REFUSAL_CODES.includes(error.code)
                    ? error.code
                    : undefined,
              }
            : message
        ),
      })),
    onSettled: () => invalidateInboxDebounced(queryClient),
  });

  const discard = useCallback(
    (messageId: string) =>
      update((thread) => ({
        ...thread,
        local: thread.local.filter((message) => message.id !== messageId),
      })),
    [update]
  );

  return { send: mutation.mutate, discard, isSending: mutation.isPending };
}
